/**
 * Core Astronomical Calculations
 * 
 * Robust, accurate astronomical calculations based on:
 * - Meeus, J. (1998). "Astronomical Algorithms"
 * - IAU 2000/2006 models for nutation and precession
 * - VSOP87 theory for planetary positions
 * 
 * All calculations use:
 * - UTC for input dates
 * - Terrestrial Time (TT) for internal calculations
 * - Proper coordinate transformations
 * - Astronomical corrections (nutation, obliquity, aberration)
 */

// ============================================================================
// TIME SYSTEM CONVERSIONS
// ============================================================================

/**
 * Convert UTC Date to Terrestrial Time (TT)
 * 
 * TT = UTC + ΔT
 * ΔT varies over time (currently ~69 seconds in 2024)
 * 
 * For precise calculations, we need TT instead of UTC
 */
export function utcToTerrestrialTime(utcDate: Date): Date {
  // ΔT in seconds (approximate, varies over time)
  // More precise values can be calculated using polynomial formulas
  const year = utcDate.getFullYear();
  let deltaT = 0;
  
  // Approximate ΔT calculation (in seconds)
  // For 2000-2100: ΔT ≈ 64 + 0.3 * (year - 2000)
  if (year >= 2000 && year <= 2100) {
    deltaT = 64 + 0.3 * (year - 2000);
  } else if (year >= 1900 && year < 2000) {
    deltaT = -2 + 0.3 * (year - 1900);
  } else {
    // Default to current approximate value
    deltaT = 69;
  }
  
  // Add ΔT milliseconds to UTC date
  const ttDate = new Date(utcDate.getTime() + deltaT * 1000);
  return ttDate;
}

/**
 * Calculate Julian Day Number from UTC Date
 * 
 * Always uses UTC components, not local time
 * Formula from Meeus, "Astronomical Algorithms" (1998)
 * 
 * @param date UTC Date object (or will be converted to UTC)
 * @returns Julian Day Number
 */
export function calculateJulianDayUTC(date: Date): number {
  // Ensure we're working with UTC
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const hour = date.getUTCHours();
  const minute = date.getUTCMinutes();
  const second = date.getUTCSeconds();
  const millisecond = date.getUTCMilliseconds();
  
  const decimalDay = day + hour / 24 + minute / 1440 + second / 86400 + millisecond / 86400000;
  
  let y = year;
  let m = month;
  
  if (month <= 2) {
    y = year - 1;
    m = month + 12;
  }
  
  const a = Math.floor(y / 100);
  const b = 2 - a + Math.floor(a / 4);
  
  const jd = Math.floor(365.25 * (y + 4716)) + 
             Math.floor(30.6001 * (m + 1)) + 
             decimalDay + b - 1524.5;
  
  return jd;
}

/**
 * Calculate Julian Day Number in Terrestrial Time
 * 
 * For astronomical calculations, we need JD in TT
 */
export function calculateJulianDayTT(date: Date): number {
  const ttDate = utcToTerrestrialTime(date);
  return calculateJulianDayUTC(ttDate);
}

/**
 * Calculate Julian Centuries since J2000.0
 * 
 * T = (JD - 2451545.0) / 36525.0
 */
export function julianCenturiesSinceJ2000(jd: number): number {
  return (jd - 2451545.0) / 36525.0;
}

// ============================================================================
// OBLIQUITY OF ECLIPTIC
// ============================================================================

/**
 * Calculate mean obliquity of ecliptic
 * 
 * Formula from Meeus, "Astronomical Algorithms"
 * Returns obliquity in degrees
 * 
 * @param T Julian centuries since J2000.0
 * @returns Mean obliquity in degrees
 */
export function meanObliquityOfEcliptic(T: number): number {
  // Mean obliquity formula (IAU 2000)
  const U = T / 100; // Julian millennia
  const e0 = 23.4392911111 - 
             0.0130041667 * U -
             0.00000016389 * U * U +
             0.00000050361 * U * U * U;
  
  return e0; // degrees
}

/**
 * Calculate true obliquity of ecliptic (with nutation)
 * 
 * @param T Julian centuries since J2000.0
 * @param deltaEpsilon Nutation in obliquity (degrees)
 * @returns True obliquity in degrees
 */
export function trueObliquityOfEcliptic(T: number, deltaEpsilon: number): number {
  const e0 = meanObliquityOfEcliptic(T);
  return e0 + deltaEpsilon;
}

// ============================================================================
// NUTATION
// ============================================================================

/**
 * Calculate nutation in longitude and obliquity
 * 
 * Simplified calculation using main terms
 * For full accuracy, use IAU 2000/2006 nutation series
 * 
 * @param T Julian centuries since J2000.0
 * @returns {deltaPsi: nutation in longitude (degrees), deltaEpsilon: nutation in obliquity (degrees)}
 */
export function calculateNutation(T: number): { deltaPsi: number; deltaEpsilon: number } {
  // Mean anomaly of Moon
  const D = (297.85036 + 445267.111480 * T + 0.0019142 * T * T) % 360;
  const D_rad = D * Math.PI / 180;
  
  // Mean anomaly of Sun
  const M = (357.52772 + 35999.050340 * T - 0.0001603 * T * T) % 360;
  const M_rad = M * Math.PI / 180;
  
  // Mean argument of latitude of Moon
  const F = (93.2720950 + 483202.0175233 * T - 0.0036539 * T * T) % 360;
  const F_rad = F * Math.PI / 180;
  
  // Mean elongation of Moon from Sun
  const Omega = (125.04452 - 1934.136261 * T + 0.0020708 * T * T) % 360;
  const Omega_rad = Omega * Math.PI / 180;
  
  // Main nutation terms (simplified - full series has 100+ terms)
  // These are the largest terms
  
  // Long period term (18.6 years)
  const deltaPsi = (-17.2 * Math.sin(Omega_rad) - 1.32 * Math.sin(2 * F_rad) - 0.23 * Math.sin(2 * D_rad) + 0.21 * Math.sin(2 * M_rad)) / 3600;
  
  const deltaEpsilon = (9.2 * Math.cos(Omega_rad) + 0.57 * Math.cos(2 * F_rad) + 0.10 * Math.cos(2 * D_rad) - 0.09 * Math.cos(2 * M_rad)) / 3600;
  
  return {
    deltaPsi, // degrees
    deltaEpsilon // degrees
  };
}

// ============================================================================
// COORDINATE TRANSFORMATIONS
// ============================================================================

/**
 * Convert equatorial coordinates (RA, Dec) to ecliptic coordinates (λ, β)
 * 
 * @param ra Right Ascension in degrees
 * @param dec Declination in degrees
 * @param epsilon Obliquity of ecliptic in degrees
 * @returns {lambda: ecliptic longitude (degrees), beta: ecliptic latitude (degrees)}
 */
export function equatorialToEcliptic(
  ra: number,
  dec: number,
  epsilon: number
): { lambda: number; beta: number } {
  const ra_rad = ra * Math.PI / 180;
  const dec_rad = dec * Math.PI / 180;
  const epsilon_rad = epsilon * Math.PI / 180;
  
  const sinBeta = Math.sin(dec_rad) * Math.cos(epsilon_rad) - 
                  Math.cos(dec_rad) * Math.sin(epsilon_rad) * Math.sin(ra_rad);
  const beta = Math.asin(sinBeta) * 180 / Math.PI;
  
  const y = Math.sin(ra_rad) * Math.cos(epsilon_rad) + 
            Math.tan(dec_rad) * Math.sin(epsilon_rad);
  const x = Math.cos(ra_rad);
  
  let lambda = Math.atan2(y, x) * 180 / Math.PI;
  if (lambda < 0) lambda += 360;
  
  return { lambda, beta };
}

/**
 * Convert ecliptic coordinates (λ, β) to equatorial coordinates (RA, Dec)
 * 
 * @param lambda Ecliptic longitude in degrees
 * @param beta Ecliptic latitude in degrees
 * @param epsilon Obliquity of ecliptic in degrees
 * @returns {ra: Right Ascension in degrees, dec: Declination in degrees}
 */
export function eclipticToEquatorial(
  lambda: number,
  beta: number,
  epsilon: number
): { ra: number; dec: number } {
  const lambda_rad = lambda * Math.PI / 180;
  const beta_rad = beta * Math.PI / 180;
  const epsilon_rad = epsilon * Math.PI / 180;
  
  const sinDec = Math.sin(beta_rad) * Math.cos(epsilon_rad) + 
                 Math.cos(beta_rad) * Math.sin(epsilon_rad) * Math.sin(lambda_rad);
  const dec = Math.asin(sinDec) * 180 / Math.PI;
  
  const y = Math.sin(lambda_rad) * Math.cos(epsilon_rad) - 
            Math.tan(beta_rad) * Math.sin(epsilon_rad);
  const x = Math.cos(lambda_rad);
  
  let ra = Math.atan2(y, x) * 180 / Math.PI;
  if (ra < 0) ra += 360;
  
  return { ra, dec };
}

/**
 * Convert hour angle and declination to altitude and azimuth
 * 
 * @param ha Hour angle in degrees
 * @param dec Declination in degrees
 * @param latitude Observer latitude in degrees
 * @returns {altitude: altitude in degrees, azimuth: azimuth in degrees}
 */
export function hourAngleToHorizontal(
  ha: number,
  dec: number,
  latitude: number
): { altitude: number; azimuth: number } {
  const ha_rad = ha * Math.PI / 180;
  const dec_rad = dec * Math.PI / 180;
  const lat_rad = latitude * Math.PI / 180;
  
  const sinAlt = Math.sin(lat_rad) * Math.sin(dec_rad) + 
                 Math.cos(lat_rad) * Math.cos(dec_rad) * Math.cos(ha_rad);
  const altitude = Math.asin(sinAlt) * 180 / Math.PI;
  
  const y = -Math.sin(ha_rad);
  const x = Math.cos(ha_rad) * Math.sin(lat_rad) - Math.tan(dec_rad) * Math.cos(lat_rad);
  
  let azimuth = Math.atan2(y, x) * 180 / Math.PI;
  if (azimuth < 0) azimuth += 360;
  
  return { altitude, azimuth };
}

// ============================================================================
// SIDEREAL TIME
// ============================================================================

/**
 * Calculate Greenwich Mean Sidereal Time (GMST)
 * 
 * @param jd Julian Day Number
 * @returns GMST in degrees
 */
export function greenwichMeanSiderealTime(jd: number): number {
  const T = julianCenturiesSinceJ2000(jd);
  
  // GMST formula (IAU 2000)
  const theta0 = 280.46061837 + 
                 360.98564736629 * (jd - 2451545.0) +
                 0.000387933 * T * T -
                 T * T * T / 38710000;
  
  // Normalize to 0-360
  let gmst = theta0 % 360;
  if (gmst < 0) gmst += 360;
  
  return gmst; // degrees
}

/**
 * Calculate Local Sidereal Time (LST)
 * 
 * @param jd Julian Day Number
 * @param longitude Observer longitude in degrees (positive = East)
 * @returns LST in degrees
 */
export function localSiderealTime(jd: number, longitude: number): number {
  const gmst = greenwichMeanSiderealTime(jd);
  const lst = (gmst + longitude) % 360;
  return lst < 0 ? lst + 360 : lst;
}

// ============================================================================
// ABERRATION
// ============================================================================

/**
 * Calculate annual aberration correction
 * 
 * Simplified calculation - full calculation requires Earth's orbital elements
 * 
 * @param T Julian centuries since J2000.0
 * @param lambda Ecliptic longitude in degrees
 * @param beta Ecliptic latitude in degrees
 * @returns {deltaLambda: correction in longitude (degrees), deltaBeta: correction in latitude (degrees)}
 */
export function calculateAberration(
  T: number,
  lambda: number,
  beta: number
): { deltaLambda: number; deltaBeta: number } {
  // Mean longitude of Sun (simplified)
  const L = (280.460 + 36000.770 * T) % 360;
  const L_rad = L * Math.PI / 180;
  
  const lambda_rad = lambda * Math.PI / 180;
  const beta_rad = beta * Math.PI / 180;
  
  // Aberration constant (arcseconds)
  const k = 20.49552;
  
  // Annual aberration corrections (simplified)
  const deltaLambda = (-k * Math.cos(L_rad - lambda_rad) / Math.cos(beta_rad)) / 3600;
  const deltaBeta = (-k * Math.sin(beta_rad) * Math.sin(L_rad - lambda_rad)) / 3600;
  
  return { deltaLambda, deltaBeta }; // degrees
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Normalize angle to 0-360 degrees
 */
export function normalizeAngle(angle: number): number {
  let normalized = angle % 360;
  if (normalized < 0) normalized += 360;
  return normalized;
}

/**
 * Normalize angle to -180 to +180 degrees
 */
export function normalizeAngle180(angle: number): number {
  let normalized = angle % 360;
  if (normalized > 180) normalized -= 360;
  if (normalized < -180) normalized += 360;
  return normalized;
}

/**
 * Convert degrees to radians
 */
export function degToRad(degrees: number): number {
  return degrees * Math.PI / 180;
}

/**
 * Convert radians to degrees
 */
export function radToDeg(radians: number): number {
  return radians * 180 / Math.PI;
}

